import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { withRouter } from 'react-router-dom';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { MLSignNowService, MLBundleService } from 'ml-services-common-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import PATrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PATrackEvent';
import HOTrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/HOTrackEvent';
import PUMBTrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PUMBTrackEvent';

import metadata from './ESignaturePage.metadata.json5';
import messages from './ESignaturePage.messages';
import styles from './ESignaturePage.module.scss'
import entryPopupMetadata from './ESignatureEntryPopup.metadata.json5';

const {
    MLSignNowResponsePath
} = appConfig;

const ESignaturePage = (props) => {
    const modalApi = useModal();
    const {
        wizardData: submissionVM, goNext, isSkipping, history
    } = props;

    const translator = useTranslator();

    const [bindIssues, setBindIssues] = useState(null);

    useEffect(() => {
        if (bindIssues) {
            throw bindIssues;
        }
    }, [bindIssues]);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const [showSignNow, setShowSignNow] = useState(false);
    const [signNowURL, setSignNowURL] = useState(null);
    const [redirectURLUnencoded, setRedirectURLUnencoded] = useState(null);

    const isQnB = appConfig.portalCode === 'QB';
    const iFrameWidthQB = isQnB ? '900px' : '1300px'

    const allForms = _.get(submissionVM, 'bindData.forms_ML.value', []);
    const spouseSignerForms = allForms.filter((form) => {
        return form.esigned_ML === true && form.spouseEmailRequired === true;
    });
    const secondaryInsuredSignerForms = allForms.filter((form) => {
        return form.esigned_ML === true && form.secondaryInsuredEmailRequired === true;
    });

    useEffect(() => {

        if (!isQnB) return;

        showProgressDialog(platformMessages.loading);

        const timer = setTimeout(() => {
            hideProgressDialog();
        }, 15000);

        return () => {
            clearTimeout(timer);
            hideProgressDialog();
        };
    }, []);

    const onFinishLater = () => {
        window.location.href = 'https://www.myautohome.metlife.com/#/retrieve_quote';
    };

    const continueToIFrame = () => {
        setShowSignNow(true);
    };

    const isSpouseSignerRequired = useCallback(() => {
        return _.get(submissionVM, 'baseData.primaryNamedInsured_ML.maritalStatus.value.code') === 'M'
            && !(_.isEmpty(spouseSignerForms));
    }, [submissionVM, spouseSignerForms]);

    const isSecondaryInsuredSignerRequired = useCallback(() => {
        return _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.accountHolder', null) !== null
            && !(_.isEmpty(secondaryInsuredSignerForms));
    }, [submissionVM, secondaryInsuredSignerForms]);

    const requireMultipleSigners = useCallback(() => {
        return isSpouseSignerRequired() || isSecondaryInsuredSignerRequired();
    }, [isSpouseSignerRequired, isSecondaryInsuredSignerRequired]);

    let showExitPopup;

    const showEntryPopup = useCallback(() => {
        const overrides = {
            additionalRequiredSignatures: {
                visible: requireMultipleSigners()
            },
            continueSignAndFinishPolicy: {
                content: <FormattedMessage
                    {...messages.signAndFinishPolicy}
                    values={{
                        continue: <strong>{translator(messages.continue).toUpperCase()}</strong>
                    }}
                />
            }
        };

        const componentProps = {
            title: translator(messages.entryPopupTitle),
            uiProps: entryPopupMetadata.pageContent,
            overrideProps: overrides,
            actionBtnLabel: messages.continue,
            onSaveAction: () => {
                continueToIFrame();
                return true;
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        // eslint-disable-next-line no-use-before-define
        ).catch(showExitPopup);
    }, [requireMultipleSigners, showExitPopup, translator]);

    showExitPopup = useCallback(() => {
        const componentProps = {
            title: translator(messages.exitPopupTitle),
            subtitle: translator(messages.exitPopupSubtitle),
            uiProps: {},
            actionBtnLabel: messages.finishLater,
            cancelBtnLabel: messages.cancel,
            onSaveAction: () => {
                onFinishLater();
                return true;
            }
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(showEntryPopup);
    }, [showEntryPopup, translator]);

    const onNext = useCallback(() => {
        if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
            HOTrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'ESignature Page' }, true);
        }
        else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
            PUMBTrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'ESignature Page' }, true);
        }
        else {
            PATrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'ESignature Page' }, true);
        }

        if (submissionVM.isBundle_ML.value && MLBundleService.isJobPostQuoteComplete(submissionVM.quoteID.value)) {
            MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(),
                `${MLTagManagerService.getFormName()}_${MLTagManagerService.getStepName()}`);
            const promise = LoadSaveService.preBind(submissionVM.value).then((res) => {
                history.push('/bundle-cart', {
                    accountNumber: submissionVM.baseData.accountNumber.value,
                    sessionUUID: submissionVM.sessionUUID.value,
                    quoteID: submissionVM.quoteID.value
                });
            });
            showProgressDialog(platformMessages.saveSubmission, promise);
        } else {
            const promise = LoadSaveService.bindSubmission(submissionVM.value).then((res) => {
                submissionVM.value = res;
                goNext();
            });
            showProgressDialog(messages.processingPayment, promise);
            promise.catch((error) => {
                setBindIssues(error);
            });
        }
    }, [goNext, history, showProgressDialog, submissionVM]);

    const onLoadSignNowFrame = useCallback(() => {
        const signNowRequest = {
            quoteID: submissionVM.quoteID.value,
            sessionUUID: submissionVM.sessionUUID.value
        };

        let url = "";
        try {
            const frame = document.getElementById('signNowFrame');
            url = frame.contentWindow.location.href;
        } catch (error) {
            // do nothing - this is needed to prevent DOMException error message when iframe first loads
        }

        if (url === redirectURLUnencoded) {
          MLSignNowService.markFormsAsSigned(signNowRequest)
            .then(() => {
              MLBundleService.setJobPostQuoteComplete(
                submissionVM.quoteID.value,
              );
              onNext();
            }).catch((error) => {
                console.error("markFormsAsSigned failed:", error);
                setBindIssues(error);
            });
        }
    }, [onNext, redirectURLUnencoded, submissionVM]);

    const skipOnResumeIf = () => {
        return !!_.get(submissionVM, 'bindData.policyNumber.value') && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding');
    };

    // init
    useEffect(() => {
        const signNowRequest = {
            quoteID: submissionVM.quoteID.value,
            sessionUUID: submissionVM.sessionUUID.value
        };

        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            MLSignNowService.getSigningURL(signNowRequest)
              .then((result) => {
                const redirectURL = `${window.location.protocol}//${window.location.host}${MLSignNowResponsePath}`;
                setRedirectURLUnencoded(
                  `${window.location.protocol}//${window.location.host}${MLSignNowResponsePath}`,
                );
                const uri = encodeURIComponent(redirectURL);
                setSignNowURL(`${result}&redirect_uri=${uri}`);
              }).catch((error) => {
                  console.error("getSigningURL failed:", error);
                  setBindIssues(error);
              });
            showEntryPopup();
        }

        if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
            HOTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'ESignature Page'}, true);
        }
        else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
            PUMBTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'ESignature Page'}, true);
        }
        else {
            PATrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'ESignature Page' }, true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const overrides = {
        loading: {
            visible: !showSignNow || signNowURL === null
        },
        signNowFrame: {
            visible: showSignNow && signNowURL !== null,
            src: signNowURL,
            height: iFrameWidthQB,
            onLoad: onLoadSignNowFrame,
            className: isQnB ? styles.qndIframeHeight : ''
        }
    };


    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            skipWhen={skipOnResumeIf}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
            />
        </WizardPage>
    );
};

ESignaturePage.propTypes = wizardProps;
export default withRouter(ESignaturePage);
